import React, { useState, useRef, useEffect, useCallback } from "react";
import { PermissionAction } from "../../actions/permission"; // adjust path to match your project
import { Container, Button, Paper, Checkbox, FormControlLabel, LinearProgress } from "@material-ui/core";
import { Autocomplete as MuiAutocomplete, TextField as MuiTextField } from "@mui/material";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ShowChartIcon from "@material-ui/icons/ShowChart";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { FaFileExcel } from "react-icons/fa6";
import { BsFileEarmarkPdfFill } from "react-icons/bs";
import DownloadIcon from "@mui/icons-material/Download";
import moment from "moment";
import DataTable from "../UserManagement/SubUserManagement/Datatable";
import { FmsDashboardAction } from "../../actions/Fmsdashboardaction";
import { FilterField } from "../TripConfiguration/util";
import FmsFilterDrawer from "./FmsFilterDrawer";
import IconTotalWireCut from "./Dashboard icon FMS/Total Wirecut.svg";
import IconConfirmedDroppage from "./Dashboard icon FMS/Confirmed Droppage.svg";
import IconUncheckedDroppage from "./Dashboard icon FMS/Unchecked Droppage.svg";
import IconExpectedConsumption from "./Dashboard icon FMS/Expected Consumption.svg";
import IconActualConsumption from "./Dashboard icon FMS/Actual Consumption.svg";
import IconTotalDeviation from "./Dashboard icon FMS/Total Deviation.svg";
import IconTotalFuelFill from "./Dashboard icon FMS/Total Fuel Fill.svg";

// ── Stat card (Total WireCut, Confirmed Droppage, etc.) ────────────
const STAT_COLORS = {
    blue: { bg: "linear-gradient(135deg,#eff6ff,#dbeafe)", color: "#1e40af" },
    red: { bg: "linear-gradient(135deg,#fef2f2,#fee2e2)", color: "#b91c1c" },
    green: { bg: "linear-gradient(135deg,#ecfdf5,#d1fae5)", color: "#15803d" },
    teal: { bg: "linear-gradient(135deg,#ecfeff,#cffafe)", color: "#0e7490" },
};

const datePresetOptions = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "This Week", value: "thisWeek" },
    { label: "Last Week", value: "lastWeek" },
    { label: "This Month", value: "thisMonth" },
    { label: "Last Month", value: "lastMonth" },
];


const statusOptions = [
    { name: "All", value: null },
    { name: "Fuel Wirecut", value: "FUEL_WIRECUT" },
    { name: "Exclude Wirecut", value: "EXCLUDE_WIRECUT" },
];

const StatCard = ({ label, value, color = "blue", icon }) => {
    const s = STAT_COLORS[color] || STAT_COLORS.blue;
    return (
        <div
            style={{
                flex: 1,
                minWidth: 160,
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                borderRadius: 10,
                background: s.bg,
                boxShadow: `inset 0 0 0 1px ${s.color}22`,
            }}
        >
            <div
                style={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: s.color,
                    fontWeight: 800,
                }}
            >
                {icon ? (
                    <img src={icon} alt="" style={{ width: 22, height: 22, objectFit: "contain" }} />
                ) : (
                    "•"
                )}
            </div>
            <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>{label}</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: s.color }}>{value}</div>
            </div>
        </div>
    );
};

const todayStr = () => moment().format("YYYY-MM-DDTHH:mm");
const startOfTodayStr = () => moment().startOf("day").format("YYYY-MM-DDTHH:mm");

const fmt = (v) => (typeof v === "number" ? v.toFixed(2) : v ?? "0.00");



export default function FmsDashboard() {
    const colSettingsRef = useRef(null);
    const [showColSettings, setShowColSettings] = useState(false);
    const [headerConfigs, setHeaderConfigs] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const saved = localStorage.getItem("fms_dashboard_col_settings");
        return saved
            ? JSON.parse(saved)
            : {
                sno: true,
                vehicleDetails: true,
                driverDetails: true,
                currentFuel: true,
                runningDistance: true,
                consumptionAnalysis: true,
                droppageAnalysis: true,
                fill: true,
                action: true,
            };
    });
    const [isLoading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(startOfTodayStr());
    const [toDate, setToDate] = useState(todayStr());

    const [siteOptions, setSiteOptions] = useState([{ name: "All Site", value: null }]);
    const [siteFilter, setSiteFilter] = useState({ name: "All Site", value: null });

    const [vehicleTypeOptions, setVehicleTypeOptions] = useState([{ name: "All Vehicle Type", value: null }]);
    const [vehicleTypeFilter, setVehicleTypeFilter] = useState({ name: "All Vehicle Type", value: null });

    const [vehicleOptions, setVehicleOptions] = useState([{ name: "All Vehicle", value: null }]);
    const [vehicleFilter, setVehicleFilter] = useState({ name: "All Vehicle", value: null });
    const [datePreset, setDatePreset] = useState(datePresetOptions[0]);

    const [statusFilter, setStatusFilter] = useState(statusOptions[0]);

    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState({});
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const fetchDropdowns = useCallback(async () => {
        const [sites, types] = await Promise.all([
            FmsDashboardAction.getFmsSiteDropdown(),
            FmsDashboardAction.getFmsVehicleTypeDropdown(),
        ]);
        if (Array.isArray(sites)) {
            setSiteOptions([{ name: "All Site", value: null }, ...sites.map((s) => ({ name: s.name || s.siteName, value: s.id }))]);
        }
        if (Array.isArray(types)) {
            setVehicleTypeOptions([{ name: "All Vehicle Type", value: null }, ...types.map((t) => ({ name: t.name || t.vehicleType, value: t.id || t.vehicleType }))]);
        }
    }, []);

    const fetchVehicles = useCallback(async (siteId) => {
        const vehicles = await FmsDashboardAction.getFmsVehicleDropdown(siteId);
        console.log("vehicles",vehicles)
        if (Array.isArray(vehicles)) {
            setVehicleOptions([{ name: "All Vehicle", value: null }, ...vehicles.map((v) => ({ name: v.number, value: v.id, runningStatus: v.runningStatus,imeiNo: v.imeiNo, }))]);
        }
    }, []);

    const fetchData = useCallback(
        async (page = pageNo, size = pageSize) => {
            setLoading(true);
            try {
                const res = await FmsDashboardAction.getFmsDashboardReport({
                    fromDate: moment(fromDate).format("DD/MM/YYYY HH:mm"),
                    toDate: moment(toDate).format("DD/MM/YYYY HH:mm"),
                    siteIds: siteFilter?.value,
                    vehicleTypeIds: vehicleTypeFilter?.value,
                    vehicleIds: vehicleFilter?.value,
                    isFuelWirecut:
                        statusFilter?.value === "FUEL_WIRECUT" ? true
                            : statusFilter?.value === "EXCLUDE_WIRECUT" ? false
                                : undefined,
                    pageNo: page,
                    pageSize: size,
                });

                if (res) {
                    setRows(res.vehicleStatusList);
                    setTotalCount(res.totalElements);
                    setStats(res.stats);
                } else {
                    setRows([]);
                    setTotalCount(0);
                    setStats({});
                }
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fromDate, toDate, siteFilter, vehicleTypeFilter, vehicleFilter, statusFilter, pageNo, pageSize]
    );

    const fetchHeaderVisibility = () => {
        const stored = JSON.parse(localStorage.getItem("data"));
        if (stored && stored.id) {
            PermissionAction.getTableHeadersVisibility("FMS_DASHBOARD", stored.id).then((res) => {
                if (res && res.data && Array.isArray(res.data)) {
                    setHeaderConfigs(res.data);
                    const apiVisibility = {};
                    res.data.forEach((item) => {
                        apiVisibility[item.headerName] = item.isVisible;
                    });
                    setVisibleColumns((prev) => ({ ...prev, ...apiVisibility }));
                }
            });
        }
    };

    useEffect(() => {
        fetchHeaderVisibility();
    }, []);

    useEffect(() => {
        const handler = (e) => {
            if (colSettingsRef.current && !colSettingsRef.current.contains(e.target)) {
                setShowColSettings(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => { fetchDropdowns(); }, [fetchDropdowns]);
    useEffect(() => { fetchVehicles(siteFilter?.value); }, [siteFilter, fetchVehicles]);
    useEffect(() => { fetchData(0, pageSize); setPageNo(0); }, []); // eslint-disable-line

    const handleShow = () => { setPageNo(0); fetchData(0, pageSize); };

    const toggleColumn = async (colId) => {
        const nextVal = !visibleColumns[colId];
        setVisibleColumns((prev) => {
            const next = { ...prev, [colId]: nextVal };
            localStorage.setItem("fms_dashboard_col_settings", JSON.stringify(next));
            return next;
        });

        const stored = JSON.parse(localStorage.getItem("data"));
        if (stored && stored.id && headerConfigs.length > 0) {
            const updatedConfigs = headerConfigs.map((config) =>
                config.headerName === colId ? { ...config, isVisible: nextVal } : config
            );
            await PermissionAction.updateTableHeadersVisibility(updatedConfigs);
            fetchHeaderVisibility();
        }
    };

    const handleDatePresetChange = (preset) => {
        setDatePreset(preset);
        const fmt = "YYYY-MM-DDTHH:mm";
        switch (preset.value) {
            case "today":
                setFromDate(moment().startOf("day").format(fmt));
                setToDate(moment().format(fmt));
                break;
            case "yesterday":
                setFromDate(moment().subtract(1, "day").startOf("day").format(fmt));
                setToDate(moment().subtract(1, "day").endOf("day").format(fmt));
                break;
            case "thisWeek":
                setFromDate(moment().startOf("week").format(fmt));
                setToDate(moment().format(fmt));
                break;
            case "lastWeek":
                setFromDate(moment().subtract(1, "week").startOf("week").format(fmt));
                setToDate(moment().subtract(1, "week").endOf("week").format(fmt));
                break;
            case "thisMonth":
                setFromDate(moment().startOf("month").format(fmt));
                setToDate(moment().format(fmt));
                break;
            case "lastMonth":
                setFromDate(moment().subtract(1, "month").startOf("month").format(fmt));
                setToDate(moment().subtract(1, "month").endOf("month").format(fmt));
                break;
            default:
                break;
        }
    };

    const handleChangePage = (event, newPage) => { setPageNo(newPage); fetchData(newPage, pageSize); };
    const handleChangeRowsPerPage = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setPageSize(newSize);
        setPageNo(0);
        fetchData(0, newSize);
    };

    const getExport = async (type) => {
        const params = {
            fromDate: moment(fromDate).format("DD/MM/YYYY HH:mm"),
            toDate: moment(toDate).format("DD/MM/YYYY HH:mm"),
            siteIds: siteFilter?.value,
            vehicleTypeIds: vehicleTypeFilter?.value,
            vehicleIds: vehicleFilter?.value,
            isFuelWirecut:
                statusFilter?.value === "FUEL_WIRECUT" ? true
                    : statusFilter?.value === "EXCLUDE_WIRECUT" ? false
                        : undefined,
        };

        let blob = null;
        let filename = "";

        if (type === "excel") {
            blob = await FmsDashboardAction.exportFmsExcel(params);
            filename = "FMS_Dashboard.xlsx";
        } else if (type === "dayWiseExcel") {
            blob = await FmsDashboardAction.exportFmsDayWiseExcel(params);
            filename = "FMS_Dashboard_DayWise.xlsx";
        } else if (type === "pdf") {
            blob = await FmsDashboardAction.exportFmsPdf(params);
            filename = "FMS_Dashboard.pdf";
        }

        if (!blob) {
            alert("Export failed. Please try again.");
            return;
        }

        // trigger the browser download
        const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    /* ── Column definitions ── */
    const columns = [
        {
            id: "sno",
            label: "S.No.",
            render: (_, index) => (
                <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>{index + 1 + pageNo * pageSize}</span>
            ),
        },
        {
            id: "vehicleDetails",
            label: "Vehicle Details",
            render: (row) => (
                <div>
                    <span style={{ color: "#2563eb", fontWeight: 700, fontSize: 13.5, cursor: "pointer" }}>{row.vehicleNo}</span>
                    {row.subtitle && <div style={{ fontSize: 11, color: "#94a3b8" }}>{row.subtitle}</div>}
                    {row.site && <div style={{ fontSize: 11, color: "#94a3b8" }}>Site: {row.site}</div>}
                    {row.lastUpdated && <div style={{ fontSize: 11, color: "#94a3b8" }}>{row.lastUpdated}</div>}
                </div>
            ),
        },
        {
            id: "driverDetails",
            label: "Driver Details",
            render: (row) => (
                <div>
                    <div style={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>{row.driver}</div>
                    {row.driverPhone && <div style={{ fontSize: 11.5, color: "#94a3b8" }}>{row.driverPhone}</div>}
                </div>
            ),
        },
        {
            id: "currentFuel",
            label: "Current Fuel (Ltrs)",
            render: (row) => (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 130 }}>
                    <div style={{ fontSize: 11.5, color: "#64748b" }}>Tank: {row.tankCapacity ?? "—"}</div>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "3px 10px",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 700,
                            width: "fit-content",
                            color: row.disabled ? "#64748b" : "#fff",
                            background: row.disabled ? "#e2e8f0" : row.currentFuelColor || "#16a34a",
                        }}
                    >
                        {row.currentFuel}
                    </div>
                    <LinearProgress
                        variant="determinate"
                        value={row.fuelPct ?? 0}
                        style={{
                            height: 6,
                            borderRadius: 3,
                            visibility: row.fuelPct == null ? "hidden" : "visible",
                            backgroundColor: "#e2e8f0",
                        }}
                    />
                </div>
            ),
        },
        {
            id: "runningDistance",
            label: "Running Distance (Km/ltr)",
            render: (row) => {
                const rd = row.runningDistance;
                if (typeof rd === "string") return <span style={{ color: "#cbd5e1", fontSize: 11 }}>{rd}</span>;
                if (!rd) return <span style={{ color: "#cbd5e1", fontSize: 11 }}>—</span>;
                return (
                    <div>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>
                            {rd.distanceKm} ({rd.engineHours} Hrs)
                        </div>
                        <div style={{ fontSize: 11.5, color: "#334155" }}>
                            <b>Start km:</b> {rd.startKm} ({rd.startFuel})
                        </div>
                        <div style={{ fontSize: 11.5, color: "#334155" }}>
                            <b>End km:</b> {rd.endKm} ({rd.endFuel})
                        </div>
                    </div>
                );
            },
        },
        {
            id: "consumptionAnalysis",
            label: "Consumption Analysis",
            render: (row) => {
                const c = row.consumption;
                if (typeof c === "string") return <span style={{ color: "#cbd5e1", fontSize: 12 }}>{c}</span>;
                if (!c) return <span style={{ color: "#cbd5e1", fontSize: 12 }}>no trip yet</span>;
                const accent = c.devDir === "down" ? "#dc2626" : "#16a34a";
                return (
                    <div>
                        <div style={{ fontSize: 12, color: "#334155" }}><b>Expected:</b> {c.expected} ({c.expectedKmpl} km/ltr)</div>
                        <div style={{ fontSize: 12, color: "#334155" }}><b>Actual:</b> {c.actual} ({c.actualKmpl} km/ltr)</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: accent }}>
                            Deviation: {c.dev} {c.devDir === "down" ? "↓" : "↑"}
                        </div>
                    </div>
                );
            },
        },
        {
            id: "droppageAnalysis",
            label: "Droppage Analysis",
            render: (row) => {
                const d = row.droppage;
                if (typeof d === "string") return <span style={{ color: "#cbd5e1", fontSize: 12 }}>{d}</span>;
                if (!d) return <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>;
                return (
                    <div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>Confirmed: {d.confirmed}</div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>Unchecked: {d.unchecked}</div>
                    </div>
                );
            },
        },
        {
            id: "fill",
            label: "Fill",
            render: (row) => (
                <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {row.fill}
                    {row.fill !== "—" && row.fill !== "0.00 L" && <InfoOutlinedIcon style={{ fontSize: 14, color: "#94a3b8" }} />}
                </span>
            ),
        },
        {
            id: "action",
            label: "Action",
            render: (row) => (
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    // startIcon={<ShowChartIcon />}
                    onClick={() => {
                        localStorage.setItem("vehicleNumber", JSON.stringify(row.vehicleNo || ""));
                        localStorage.setItem("historyFromDate", new Date(fromDate));
                        localStorage.setItem("historyToDate", new Date(toDate));
                        localStorage.setItem("historyOpenPanel", "fuel"); 
                        window.open(`/history/${row.id}`, "_blank");
                    }}
                    style={{ textTransform: "none", borderRadius: 8 }}
                >
                    View
                </Button>
            ),
        },
    ];

    const visibleCols = columns.filter((col) => visibleColumns[col.id]);

    return (
        <div className="sub-user-list-root" style={{ position: "relative" }}>
            <Container>
                {/* ── Page title ── */}
                <div className="page-title-row">
                    <button className="page-back-btn" onClick={() => window.history.back()} aria-label="Go back">
                        <ArrowBackIcon className="page-back-icon" />
                    </button>
                    <span className="page-title-text">FMS Dashboard — All Vehicles</span>
                </div>

                {/* ── Main card ── */}
                <Paper className="sub-user-list-paper" id="left">
                    <div className="sl-toolbar" style={{ flexWrap: "wrap", rowGap: 10, alignItems: "center" }}>
                        <div className="sl-toolbar-left" style={{ flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
                            <MuiAutocomplete
                                size="small"
                                options={datePresetOptions}
                                value={datePreset}
                                getOptionLabel={(option) => option.label}
                                onChange={(e, newValue) => {
                                    if (newValue) handleDatePresetChange(newValue);
                                }}
                                sx={{ width: 160 }}
                                renderInput={(params) => (
                                    <MuiTextField
                                        {...params}
                                        placeholder="Today"
                                        variant="outlined"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                height: 40,
                                                borderRadius: "10px",
                                                backgroundColor: "#fff",
                                                fontSize: 13,
                                                "& fieldset": { borderColor: "#d1d5db" },
                                                "&:hover fieldset": { borderColor: "#9ca3af" },
                                                "&.Mui-focused fieldset": { borderColor: "#2563eb" },
                                            },
                                        }}
                                    />
                                )}
                            />

                            <FilterField label="From">
                                <input
                                    className="gf-date-input gf-date-input-two"
                                    type="datetime-local"
                                    value={fromDate}
                                    max={moment().format("YYYY-MM-DDTHH:mm")}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </FilterField>

                            <FilterField label="To">
                                <input
                                    className="gf-date-input gf-date-input-two"
                                    type="datetime-local"
                                    value={toDate}
                                    min={fromDate}
                                    max={moment().format("YYYY-MM-DDTHH:mm")}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </FilterField>


                            <Button
                                color="primary"
                                variant="contained"
                                style={{
                                    borderRadius: 10,
                                    height: 36,
                                    minWidth: 80,
                                    marginBottom: 3,
                                    background: "#082D6B",
                                }}
                                onClick={handleShow}
                            >
                                Show
                            </Button>
                        </div>

                        <div className="sl-toolbar-right">
                            {/* Secondary filters (Site / Vehicle Type / Vehicle / Status) live in the drawer */}

                            <FmsFilterDrawer
                                siteOptions={siteOptions}
                                siteFilter={siteFilter}
                                setSiteFilter={setSiteFilter}
                                vehicleTypeOptions={vehicleTypeOptions}
                                vehicleTypeFilter={vehicleTypeFilter}
                                setVehicleTypeFilter={setVehicleTypeFilter}
                                vehicleOptions={vehicleOptions}
                                vehicleFilter={vehicleFilter}
                                setVehicleFilter={setVehicleFilter}
                                statusOptions={statusOptions}
                                statusFilter={statusFilter}
                                setStatusFilter={setStatusFilter}
                                onApply={handleShow}
                            />

                            {/* ── Export buttons — same gradients/props as LockInventory.js ── */}
                            <Button
                                variant="contained"
                                onClick={() => getExport("dayWiseExcel")}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    height: 40,
                                    padding: "0 18px",
                                    background: "linear-gradient(135deg, #4c1d95 0%, #5b21b6 40%, #6d28d9 100%)",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: 10,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    boxShadow: "0 4px 16px rgba(76, 29, 149, 0.45)",
                                    whiteSpace: "nowrap",
                                    letterSpacing: "0.3px",
                                }}
                            >
                                Day Wise Excel
                            </Button>

                            <Button
                                variant="contained"
                                startIcon={<BsFileEarmarkPdfFill style={{ fontSize: 18 }} />}
                                endIcon={<DownloadIcon />}
                                onClick={() => getExport("pdf")}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    height: 40,
                                    padding: "0 18px",
                                    background: "linear-gradient(135deg, #7f1d1d 0%, #991b1b 30%, #b91c1c 60%, #dc2626 100%)",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: 10,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    boxShadow: "0 4px 16px rgba(127, 29, 29, 0.45)",
                                    whiteSpace: "nowrap",
                                    letterSpacing: "0.3px",
                                }}
                            >
                                PDF
                            </Button>

                            <Button
                                variant="contained"
                                startIcon={<FaFileExcel style={{ fontSize: 18 }} />}
                                endIcon={<DownloadIcon />}
                                onClick={() => getExport("excel")}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    height: 40,
                                    padding: "0 18px",
                                    background: "linear-gradient(135deg, #064e3b 0%, #065f46 30%, #047857 60%, #059669 100%)",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: 10,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    boxShadow: "0 4px 16px rgba(6, 78, 59, 0.45)",
                                    whiteSpace: "nowrap",
                                    letterSpacing: "0.3px",
                                }}
                            >
                                Excel
                            </Button>

                            <div className="gf-col-settings-wrap" ref={colSettingsRef}>
                                <button
                                    className={`gf-col-settings-btn ${showColSettings ? "active" : ""}`}
                                    onClick={() => setShowColSettings(!showColSettings)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        height: 40,
                                        padding: "0 16px",
                                        border: "none",
                                        borderRadius: 10,
                                        background: showColSettings
                                            ? "linear-gradient(135deg, #0f172a 0%, #1e293b 30%, #334155 60%, #475569 100%)"
                                            : "linear-gradient(135deg, #1e293b 0%, #334155 30%, #475569 60%, #64748b 100%)",
                                        color: "#ffffff",
                                        fontSize: 14,
                                        fontWeight: 600,
                                        cursor: "pointer",
                                        fontFamily: "inherit",
                                        boxShadow: "0 4px 16px rgba(30, 41, 59, 0.4)",
                                        letterSpacing: "0.3px",
                                    }}
                                >
                                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                                        <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                    </svg>
                                    Columns
                                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12">
                                        <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                {showColSettings && (
                                    <div className="gf-col-dropdown">
                                        <div className="gf-col-dropdown-header">Toggle Columns</div>
                                        {columns.map((col) => (
                                            <label key={col.id} className="gf-col-dropdown-item">
                                                <input
                                                    type="checkbox"
                                                    className="gf-col-checkbox"
                                                    checked={visibleColumns[col.id]}
                                                    onChange={() => toggleColumn(col.id)}
                                                />
                                                {col.label}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Stat cards ── */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, padding: "0px 16px 0" }}>
                        <StatCard label="Total WireCut" value={stats.totalWireCut ?? 0} color="blue" icon={IconTotalWireCut} />
                        <StatCard label="Confirmed Droppage" value={`${fmt(stats.confirmedDroppage)} Ltrs`} color="red" icon={IconConfirmedDroppage} />
                        <StatCard label="Unchecked Droppage" value={`${fmt(stats.uncheckedDroppage)} Ltrs`} color="red" icon={IconUncheckedDroppage} />
                        <StatCard label="Expected Consumption" value={`${fmt(stats.expectedConsumption)} Ltrs`} color="green" icon={IconExpectedConsumption} />
                        <StatCard label="Actual Consumption" value={`${fmt(stats.actualConsumption)} Ltrs`} color="green" icon={IconActualConsumption} />
                        <StatCard label="Total Deviation" value={`${fmt(stats.totalDeviation)} Ltrs`} color="green" icon={IconTotalDeviation} />
                        <StatCard label="Total Fuel Fill" value={`${fmt(stats.totalFuelFill)} Ltrs`} color="teal" icon={IconTotalFuelFill} />
                    </div>

                    {isLoading && <LinearProgress style={{ margin: "10px 16px 0" }} />}

                    {/* ── Table ── */}
                    <div style={{ padding: "10px 0 0" }}>
                        <DataTable
                            columns={visibleCols}
                            rows={rows}
                            rowKey={(row) => row.id}
                            tableId="fms-dashboard-table"
                            page={pageNo}
                            rowsPerPage={pageSize}
                            totalCount={totalCount}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            showRowMenu={false}
                        />
                    </div>
                </Paper>
            </Container>
        </div>
    );
}